/**
 * Story mode (v8-visual). Pre-scripted demo narratives that orchestrate a sequence
 * of /control/* calls with captions, letting a presenter tell a coherent story in one click.
 *
 * Stories drive the same operator-gated /control/* surface as the manual panel (control.ts),
 * so they share controlFetch — the token is read from the same lock, and a 401/503 re-locks
 * the whole panel with the same message instead of a story silently doing nothing.
 */

import { controlFetch, getToken, renderLockState } from './control.js';

interface StoryStep {
  delayMs: number;
  caption: string;
  action: (doc: Document) => Promise<void>;
}

export interface Story {
  id: string;
  label: string;
  steps: StoryStep[];
}

async function ctrl(doc: Document, path: string, body?: unknown): Promise<void> {
  await controlFetch(doc, path, {
    method: 'POST',
    ...(body != null
      ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }
      : {}),
  });
}

export const STORIES: Story[] = [
  {
    id: 'sanctions_intercept',
    label: '⛔ Sanctions intercept',
    steps: [
      {
        delayMs: 0,
        caption: 'Starting engine with 8 clients...',
        action: async (doc) => {
          await ctrl(doc, '/control/start');
          await ctrl(doc, '/control/config', { concurrency: 8 });
        },
      },
      {
        delayMs: 5000,
        caption: 'Traffic flowing — ramping up to 20 clients...',
        action: async (doc) => { await ctrl(doc, '/control/config', { concurrency: 20 }); },
      },
      {
        delayMs: 9000,
        caption: 'Firing a €1 payment to Hamed ABDOLLAHI (sanctioned creditor)...',
        action: async (doc) => { await ctrl(doc, '/control/trigger', { kind: 'sanctions_payment' }); },
      },
      {
        delayMs: 12000,
        caption: '⛔ Gateway returned 403 SANCTIONS_HIT — check the feed!',
        action: async () => {},
      },
      {
        delayMs: 22000,
        caption: 'Easing off and stopping run.',
        action: async (doc) => { await ctrl(doc, '/control/stop'); },
      },
    ],
  },
  {
    id: 'cross_border_surge',
    label: '🌍 Cross-border surge',
    steps: [
      {
        delayMs: 0,
        caption: 'Starting with cross-border flows only...',
        action: async (doc) => {
          await ctrl(doc, '/control/start');
          await ctrl(doc, '/control/config', { concurrency: 15 });
          for (const k of ['sdd', 'swift', 'p2p']) await ctrl(doc, '/control/flow', { kind: k, on: false });
        },
      },
      {
        delayMs: 6000,
        caption: 'Watch the Flows panel fill with multi-step FX lifecycles...',
        action: async () => {},
      },
      {
        delayMs: 18000,
        caption: 'Re-enabling all flow kinds and stopping.',
        action: async (doc) => {
          for (const k of ['sdd', 'swift', 'p2p']) await ctrl(doc, '/control/flow', { kind: k, on: true });
          await ctrl(doc, '/control/stop');
        },
      },
    ],
  },
  {
    id: 'breaker_trip',
    label: '💥 Breaker trip',
    steps: [
      {
        delayMs: 0,
        caption: 'Starting normal traffic at 15 clients...',
        action: async (doc) => {
          await ctrl(doc, '/control/start');
          await ctrl(doc, '/control/config', { concurrency: 15 });
        },
      },
      {
        delayMs: 7000,
        caption: 'Injecting a health crash — watch the breaker trip! 💥',
        action: async (doc) => { await ctrl(doc, '/control/fault', { kind: 'health_crash', on: true }); },
      },
      {
        delayMs: 11000,
        caption: 'Both banks unreachable. Engine halted. Traffic draining...',
        action: async () => {},
      },
      {
        delayMs: 17000,
        caption: 'Clearing fault — banks recovering...',
        action: async (doc) => { await ctrl(doc, '/control/fault/clear'); },
      },
      {
        delayMs: 24000,
        caption: 'Story complete. Stopping run.',
        action: async (doc) => { await ctrl(doc, '/control/stop'); },
      },
    ],
  },
  {
    id: 'corporate_governance',
    label: '🏢 Corporate dual-control',
    steps: [
      {
        delayMs: 0,
        caption: 'Starting simulator with 10 clients...',
        action: async (doc) => {
          await ctrl(doc, '/control/start');
          await ctrl(doc, '/control/config', { concurrency: 10 });
        },
      },
      {
        delayMs: 5000,
        caption: 'Charlie (Initiator) fires a €150 payment (> €100 threshold)...',
        action: async (doc) => {
          await ctrl(doc, '/control/fire', { debtor: 'charlie', scenario: 'sct', amountCents: 15000 });
        },
      },
      {
        delayMs: 10000,
        caption: 'Payment parked in PENDING_APPROVAL with a 24h Ledger hold. Static SoD protects against self-approval.',
        action: async () => {},
      },
      {
        delayMs: 16000,
        caption: 'Checker approval enforces strict SCA step-up gate (428). Dual-control verified.',
        action: async (doc) => { await ctrl(doc, '/control/stop'); },
      },
    ],
  },
];

let storyTimer: ReturnType<typeof window.setTimeout> | null = null;
let storyRunning = false;

function setCaption(doc: Document, text: string): void {
  const el = doc.querySelector('[data-story-caption]');
  if (el) el.textContent = text;
}

function setRunning(doc: Document, running: boolean): void {
  storyRunning = running;
  const play = doc.querySelector<HTMLButtonElement>('[data-story-play]');
  const stop = doc.querySelector<HTMLButtonElement>('[data-story-stop]');
  if (play) play.style.display = running ? 'none' : '';
  if (stop) stop.style.display = running ? '' : 'none';
}

export function stopStory(doc: Document): void {
  if (storyTimer) { window.clearTimeout(storyTimer); storyTimer = null; }
  setRunning(doc, false);
  setCaption(doc, '');
}

/**
 * A story is a scripted sequence of /control/* calls narrated by caption. Without a token,
 * controlFetch() no-ops every one of those calls (correctly — the panel is locked) but
 * scheduleNext() would still narrate each caption on schedule regardless, producing a
 * fully-voiced demo (down to "Gateway returned 403 SANCTIONS_HIT") for a story that never
 * actually fired a single request. Gate at start, and re-check every step in case the token
 * is cleared mid-story (e.g. the server rejects it partway through).
 */
export function runStory(doc: Document, story: Story): void {
  if (!getToken()) {
    renderLockState(doc, true, 'Locked — enter the operator token before running a story.');
    return;
  }

  stopStory(doc);
  setRunning(doc, true);

  const steps = story.steps;
  let i = 0;

  function scheduleNext(): void {
    if (i >= steps.length) { setRunning(doc, false); return; }
    const step = steps[i]!;
    i++;
    storyTimer = window.setTimeout(() => {
      if (!storyRunning) return;
      if (!getToken()) { stopStory(doc); return; }
      setCaption(doc, step.caption);
      void step.action(doc).then(() => { scheduleNext(); });
    }, step.delayMs);
  }

  scheduleNext();
}

export function initStories(doc: Document = document): void {
  const select = doc.querySelector<HTMLSelectElement>('[data-story-select]');
  const playBtn = doc.querySelector<HTMLButtonElement>('[data-story-play]');
  const stopBtn = doc.querySelector<HTMLButtonElement>('[data-story-stop]');

  if (select && playBtn) {
    select.addEventListener('change', () => {
      if (playBtn) playBtn.disabled = !select.value;
    });

    playBtn.addEventListener('click', () => {
      const story = STORIES.find((s) => s.id === select.value);
      if (story) runStory(doc, story);
    });
  }

  stopBtn?.addEventListener('click', () => stopStory(doc));
}
